import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, ProviderService, UiStateService } from '@app/core/services';
import { ApplicationInsightsService, AppInsightTags } from '@app/core/services/application-insights.service';
import { ConfigService } from '@app/core/services/config.service';
import { MerakiAuthService } from '@app/core/services/meraki-auth.service';
import { BrowserTypes, SidebarLocations } from '@app/core/services/state/ui-state/ui-state.store';
import { WINDOW } from '@app/core/services/window.provider';
import { BehaviorSubject, combineLatest, EMPTY, fromEvent, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pairwise, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import {
  PracticeAgentAnalyticsDetails,
  PracticeAgentAnalyticsEventType,
  PracticeAgentHomeTab,
} from './practice-agent-analytics.models';
import { PracticeAgentAnalyticsService } from './practice-agent-analytics.service';
import {
  HbcAuthChangedMessage,
  HbcContextChangedMessage,
  HbcInitMessage,
  HbcPracticeAgentMessage,
  HbcSuppressionChangedMessage,
  isPaVisibilityChangedMessage,
  PRACTICE_AGENT_MESSAGE_VERSION,
  PracticeAgentContext,
  PracticeAgentContextPayload,
  PracticeAgentPage,
  PracticeAgentSuppression,
  PracticeAgentSuppressionMode,
} from './practice-agent-message.models';

const AUTH_REFRESH_INTERVAL_MS = 50 * 60 * 1000;
const SNOOZE_DURATION_MS = 60 * 60 * 1000;
const SUPPRESSION_RETRY_INTERVAL_MS = 60 * 1000;
const CONTEXT_RETRY_INTERVAL_MS = 60 * 1000;
const SUPPRESSION_STORAGE_KEY = 'practiceAgent.widgetSuppression.v1';
const TIMED_RETRIGGER_HOUR_SAST = 16;
const SAST_UTC_OFFSET_MS = 2 * 60 * 60 * 1000;
const TIMED_RETRIGGER_SNOOZE_WINDOW_MS = 60 * 60 * 1000;

type ContextMessageType = 'hbc.init' | 'hbc.context.changed';

interface ContextMessageRequest {
  type: ContextMessageType;
  context: PracticeAgentContext;
  sendSuppressionEnded: boolean;
  contextId: number;
}

interface PersistedSuppression {
  mode: PracticeAgentSuppressionMode;
  until: string;
}

@Injectable()
export class PracticeAgentBridgeService implements OnDestroy {
  readonly widgetUrl = this.configService.config.practiceAgent.widgetUrl || '';

  private readonly widgetOrigin = this.getWidgetOrigin(this.widgetUrl);
  private readonly snoozeDurationMs = this.getSnoozeDurationMs();
  readonly snoozeDurationLabel = this.getSnoozeDurationLabel();
  private readonly paRequestedVisibilitySubject = new BehaviorSubject(false);
  private readonly homePageActiveSubject = new BehaviorSubject(false);
  private readonly suppressedSubject = new BehaviorSubject(false);
  private readonly contextMessageRequests = new Subject<ContextMessageRequest>();
  private readonly subscriptions = new Subscription();

  private iframe: HTMLIFrameElement;
  private latestContext: PracticeAgentContext;
  private authRefreshSubscription: Subscription;
  private suppressionTimerSubscription: Subscription;
  private contextRetrySubscription: Subscription;
  private timedRetriggerSubscription: Subscription;
  private suppressionMode: PracticeAgentSuppressionMode;
  private suppressionUntil: string;
  private suppressionEndPending = false;
  private currentContextId = 0;
  private nextContextId = 0;
  private enabled = false;
  private iframeLoaded = false;
  private initialContextSent = false;
  private started = false;
  private currentHomeTab: PracticeAgentHomeTab = 'other';

  private readonly firebaseAuthenticated$ = this.merakiAuthService.authenticated$.pipe(
    map(() => true),
    startWith(false),
    distinctUntilChanged()
  );

  private readonly widgetEnabled$ = this.providerService.provider$.pipe(
    map(provider => ({
      practiceTenantId: provider?.PracticeTenantId || '',
      practiceId: provider?.PracticeId || '',
    })),
    distinctUntilChanged(
      (previous, current) =>
        previous.practiceTenantId === current.practiceTenantId && previous.practiceId === current.practiceId
    ),
    switchMap(() =>
      this.providerService.practiceAgentWidgetEnabled$.pipe(
        startWith(false),
        catchError(() => of(false))
      )
    )
  );

  private readonly providerPresent$ = this.providerService.provider$.pipe(
    map(provider => !!provider?.PracticeNumber && !!provider?.PracticeId),
    distinctUntilChanged()
  );

  readonly enabled$ = this.widgetOrigin
    ? combineLatest([
        this.authService.authenticated$,
        this.firebaseAuthenticated$,
        this.providerPresent$,
        this.widgetEnabled$,
        this.uiStateService.actualBrowserType$,
      ]).pipe(
        map(
          ([authenticated, firebaseAuthenticated, providerPresent, widgetEnabled, browserType]) =>
            authenticated &&
            firebaseAuthenticated &&
            providerPresent &&
            widgetEnabled &&
            browserType === BrowserTypes.Web
        ),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      )
    : of(false);

  readonly visible$ = combineLatest([
    this.paRequestedVisibilitySubject,
    this.homePageActiveSubject,
    this.suppressedSubject,
    this.enabled$,
  ]).pipe(
    map(
      ([paRequestedVisibility, homePageActive, suppressed, enabled]) =>
        paRequestedVisibility && homePageActive && !suppressed && enabled
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly sidebarOpen$ = this.uiStateService.sidebar$.pipe(
    map(sidebar => sidebar !== SidebarLocations.HIDDEN),
    distinctUntilChanged()
  );

  readonly wideSidebarOpen$ = this.uiStateService.sidebar$.pipe(
    map(sidebar => sidebar === SidebarLocations.CALENDAR),
    distinctUntilChanged()
  );

  private readonly currentPage$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.urlAfterRedirects || event.url),
    startWith(''),
    map(url => url || this.router.url),
    tap(url => (this.currentHomeTab = this.getCurrentHomeTab(url))),
    map(url => this.getCurrentPage(url)),
    distinctUntilChanged()
  );

  private readonly context$ = combineLatest([
    this.providerService.provider$,
    this.uiStateService.currentPatientId$,
    this.uiStateService.activePatients$,
    this.currentPage$,
  ]).pipe(
    map(([provider, currentPatientId, activePatients, currentPage]) => ({
      practiceNumber: provider?.PracticeNumber || '',
      doctorId: provider?.PracticeId || '',
      patientId: this.getContextPatientId(currentPage, currentPatientId, activePatients),
      currentPage,
    })),
    filter(context => !!context.practiceNumber && !!context.doctorId),
    distinctUntilChanged(
      (previous, current) =>
        previous.practiceNumber === current.practiceNumber &&
        previous.doctorId === current.doctorId &&
        previous.patientId === current.patientId &&
        previous.currentPage === current.currentPage
    )
  );

  constructor(
    private authService: AuthService,
    private providerService: ProviderService,
    private uiStateService: UiStateService,
    private router: Router,
    private merakiAuthService: MerakiAuthService,
    private configService: ConfigService,
    private appInsightsService: ApplicationInsightsService,
    private analyticsService: PracticeAgentAnalyticsService,
    @Inject(WINDOW) private window: Window
  ) {
    this.restorePersistedSuppression();
  }

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    if (this.widgetUrl && !this.widgetOrigin) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CONFIG_INVALID);
    }

    this.subscriptions.add(
      this.authService.authenticated$
        .pipe(
          distinctUntilChanged(),
          pairwise(),
          filter(([wasAuthenticated, authenticated]) => wasAuthenticated && !authenticated),
          tap(() => this.handleLogout())
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled =>
            enabled
              ? this.contextMessageRequests.pipe(
                  switchMap(request =>
                    this.merakiAuthService.getFirebaseToken().pipe(
                      catchError(() => of(null)),
                      map(firebaseIdToken => ({ request, firebaseIdToken }))
                    )
                  )
                )
              : EMPTY
          ),
          tap(({ request, firebaseIdToken }) => this.sendContextMessage(request, firebaseIdToken))
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(tap(enabled => this.handleEnabledChange(enabled)))
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled => (enabled ? this.context$ : EMPTY)),
          tap(context => this.handleContextChange(context))
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled => (enabled ? fromEvent<MessageEvent>(this.window, 'message') : EMPTY)),
          tap(event => this.handleMessage(event))
        )
        .subscribe()
    );
  }

  connectIframe(iframe: HTMLIFrameElement): void {
    if (!this.enabled) {
      return;
    }

    this.stopAuthRefresh();
    this.iframe = iframe;
    this.iframeLoaded = true;
    this.initialContextSent = false;
    this.currentContextId = 0;
    this.paRequestedVisibilitySubject.next(false);

    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_IFRAME_LOADED);
    this.startAuthRefresh();

    if (this.latestContext) {
      this.requestContextMessage('hbc.init', this.latestContext);
    }
  }

  handleIframeError(): void {
    this.hideWidget('iframe-load-failed');
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_IFRAME_LOAD_FAILED);
    this.stopAuthRefresh();
    this.stopContextRetry();
    this.iframe = null;
    this.iframeLoaded = false;
    this.initialContextSent = false;
  }

  dismiss(): void {
    if (!this.enabled || this.suppressedSubject.value) {
      return;
    }

    this.hideWidget('dismissed');
    this.stopSuppressionTimer();
    this.suppressionEndPending = false;
    this.suppressionMode = 'dismissed';
    this.suppressionUntil = this.getNextCalendarDayStart(this.getCurrentTime()).toISOString();
    this.suppressedSubject.next(true);
    this.persistSuppression();
    this.postMessage(this.createSuppressionMessage());
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_DISMISSED);
    this.trackAnalytics('widget-closed', { SuppressionUntil: this.suppressionUntil });
    this.resumeSuppressionTimer();
  }

  snooze(): void {
    if (!this.enabled) {
      return;
    }

    this.hideWidget('snoozed');
    this.stopSuppressionTimer();
    this.suppressionEndPending = false;
    this.suppressionMode = 'snoozed';
    this.suppressionUntil = this.getSnoozeUntil(this.getCurrentTime()).toISOString();
    this.suppressedSubject.next(true);
    this.persistSuppression();
    this.postMessage(this.createSuppressionMessage());
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_SNOOZED);
    this.trackAnalytics('widget-snoozed', {
      SnoozeDurationMs: this.snoozeDurationMs,
      SuppressionUntil: this.suppressionUntil,
    });

    this.resumeSuppressionTimer();
  }

  destroy(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.disconnectIframe(false);
    this.subscriptions.unsubscribe();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private handleEnabledChange(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.disconnectIframe(false);
      return;
    }

    this.startTimedRetriggerTimer();
    this.resumeSuppressionTimer();
  }

  trackCollapsed(collapsed: boolean): void {
    this.trackAnalytics(collapsed ? 'widget-collapsed' : 'widget-expanded');
  }

  private handleContextChange(context: PracticeAgentContext): void {
    this.hideWidget('context-changed');
    this.homePageActiveSubject.next(context.currentPage === 'home');
    this.latestContext = context;
    if (!this.iframeLoaded) {
      return;
    }

    const messageType = this.initialContextSent ? 'hbc.context.changed' : 'hbc.init';
    this.requestContextMessage(messageType, context);
  }

  private requestContextMessage(
    type: ContextMessageType,
    context: PracticeAgentContext,
    sendSuppressionEnded = false
  ): void {
    this.stopContextRetry();
    this.contextMessageRequests.next(this.createContextMessageRequest(type, context, sendSuppressionEnded));
  }

  private createContextMessageRequest(
    type: ContextMessageType,
    context: PracticeAgentContext,
    sendSuppressionEnded = false
  ): ContextMessageRequest {
    this.hideWidget('context-changed');
    const contextId = ++this.nextContextId;
    return {
      type,
      context,
      sendSuppressionEnded: sendSuppressionEnded || this.suppressionEndPending,
      contextId,
    };
  }

  private sendContextMessage(request: ContextMessageRequest, firebaseIdToken: string): void {
    if (!this.canPostMessage() || request.contextId !== this.nextContextId) {
      return;
    }

    if (!firebaseIdToken) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_AUTH_FAILED);
      if (request.sendSuppressionEnded) {
        this.scheduleSuppressionRetry();
      } else {
        this.scheduleContextRetry(request);
      }
      return;
    }

    this.stopContextRetry();

    if (request.sendSuppressionEnded) {
      this.suppressionMode = null;
      this.suppressionUntil = null;
      this.suppressionEndPending = false;
      this.suppressedSubject.next(false);
      this.clearPersistedSuppression();
    }

    this.postMessage(this.createAuthMessage(firebaseIdToken));

    if (request.sendSuppressionEnded) {
      this.postMessage(this.createSuppressionMessage());
    }

    const payload: PracticeAgentContextPayload = {
      ...request.context,
      contextId: request.contextId,
      suppression: this.getSuppression(),
    };

    const message: HbcInitMessage | HbcContextChangedMessage = {
      type: request.type,
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload,
    };

    this.postMessage(message);
    this.currentContextId = request.contextId;
    if (request.sendSuppressionEnded) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_SUPPRESSION_ENDED);
    }
    this.initialContextSent = true;
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CONTEXT_SENT, {
      messageType: request.type,
      currentPage: request.context.currentPage,
    });
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.iframe || event.source !== this.iframe.contentWindow) {
      return;
    }

    if (event.origin !== this.widgetOrigin) {
      this.trackInvalidMessage('origin');
      return;
    }

    if (!isPaVisibilityChangedMessage(event.data)) {
      this.trackInvalidMessage('payload');
      return;
    }

    if (
      event.data.payload.contextId !== this.currentContextId ||
      this.currentContextId !== this.nextContextId
    ) {
      this.trackInvalidMessage('stale-context');
      return;
    }

    const { visible, reason } = event.data.payload;
    if (reason === 'card-api-failed') {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CARD_API_FAILED);
    }

    if (visible) {
      if (this.suppressedSubject.value || !this.homePageActiveSubject.value) {
        return;
      }

      const wasVisible = this.isEffectivelyVisible();
      this.paRequestedVisibilitySubject.next(true);
      if (!wasVisible) {
        this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CARD_AVAILABLE);
        this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_SHOWN);
        this.trackAnalytics('widget-shown');
      }
      return;
    }

    this.hideWidget(reason);
  }

  private startAuthRefresh(): void {
    this.stopAuthRefresh();
    this.authRefreshSubscription = timer(AUTH_REFRESH_INTERVAL_MS, AUTH_REFRESH_INTERVAL_MS)
      .pipe(
        switchMap(() => this.merakiAuthService.getFirebaseToken().pipe(catchError(() => of(null)))),
        tap(firebaseIdToken => {
          if (firebaseIdToken && this.canPostMessage()) {
            if (this.latestContext && (this.currentContextId === 0 || this.currentContextId !== this.nextContextId)) {
              const messageType = this.initialContextSent ? 'hbc.context.changed' : 'hbc.init';
              const request = this.createContextMessageRequest(messageType, this.latestContext);
              this.sendContextMessage(request, firebaseIdToken);
            } else {
              this.postMessage(this.createAuthMessage(firebaseIdToken));
            }
          } else if (this.canPostMessage()) {
            this.invalidateCurrentContext('auth-refresh-failed');
            this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_AUTH_FAILED);
            this.scheduleLatestContextRetry();
          }
        })
      )
      .subscribe();
  }

  private stopAuthRefresh(): void {
    if (this.authRefreshSubscription) {
      this.authRefreshSubscription.unsubscribe();
      this.authRefreshSubscription = null;
    }
  }

  private endSuppression(): void {
    if (!this.suppressionMode) {
      return;
    }

    this.paRequestedVisibilitySubject.next(false);
    this.stopSuppressionTimer();

    if (this.iframeLoaded && this.latestContext) {
      this.suppressionEndPending = true;
      this.requestContextMessage('hbc.context.changed', this.latestContext, true);
      return;
    }

    this.suppressionMode = null;
    this.suppressionUntil = null;
    this.suppressionEndPending = false;
    this.suppressedSubject.next(false);
    this.clearPersistedSuppression();
  }

  private stopSuppressionTimer(): void {
    if (this.suppressionTimerSubscription) {
      this.suppressionTimerSubscription.unsubscribe();
      this.suppressionTimerSubscription = null;
    }
  }

  private resumeSuppressionTimer(): void {
    if (!this.suppressionMode || !this.suppressionUntil) {
      return;
    }

    const now = this.getCurrentTime();
    const suppressionEndMs = new Date(this.suppressionUntil).getTime();
    const remainingDurationMs = suppressionEndMs - now.getTime();
    if (remainingDurationMs <= 0) {
      this.endSuppression();
      return;
    }

    this.stopSuppressionTimer();
    if (this.suppressionMode === 'snoozed' && suppressionEndMs === this.getNextTimedRetrigger(now).getTime()) {
      return;
    }

    this.suppressionTimerSubscription = timer(remainingDurationMs)
      .pipe(tap(() => this.endSuppression()))
      .subscribe();
  }

  private startTimedRetriggerTimer(): void {
    this.stopTimedRetriggerTimer();
    const now = this.getCurrentTime();
    const delayMs = this.getNextTimedRetrigger(now).getTime() - now.getTime();
    this.timedRetriggerSubscription = timer(delayMs)
      .pipe(tap(() => this.handleTimedRetrigger()))
      .subscribe();
  }

  private handleTimedRetrigger(): void {
    this.startTimedRetriggerTimer();
    if (!this.enabled || this.suppressionMode === 'dismissed') {
      return;
    }

    if (this.suppressionMode === 'snoozed') {
      this.trackAnalytics('widget-daily-retriggered');
      this.endSuppression();
      return;
    }

    if (
      !this.iframeLoaded ||
      !this.latestContext ||
      !this.homePageActiveSubject.value ||
      this.isEffectivelyVisible()
    ) {
      return;
    }

    this.trackAnalytics('widget-daily-retriggered');
    this.requestContextMessage('hbc.context.changed', this.latestContext);
  }

  private stopTimedRetriggerTimer(): void {
    if (this.timedRetriggerSubscription) {
      this.timedRetriggerSubscription.unsubscribe();
      this.timedRetriggerSubscription = null;
    }
  }

  private scheduleSuppressionRetry(): void {
    this.stopSuppressionTimer();
    this.suppressionTimerSubscription = timer(SUPPRESSION_RETRY_INTERVAL_MS)
      .pipe(tap(() => this.endSuppression()))
      .subscribe();
  }

  private scheduleContextRetry(request: ContextMessageRequest): void {
    this.stopContextRetry();
    this.contextRetrySubscription = timer(CONTEXT_RETRY_INTERVAL_MS)
      .pipe(
        filter(() => request.contextId === this.nextContextId && this.canPostMessage()),
        tap(() => this.contextMessageRequests.next(request))
      )
      .subscribe();
  }

  private scheduleLatestContextRetry(): void {
    if (!this.latestContext) {
      return;
    }

    const messageType = this.initialContextSent ? 'hbc.context.changed' : 'hbc.init';
    const request = this.createContextMessageRequest(messageType, this.latestContext);
    this.scheduleContextRetry(request);
  }

  private stopContextRetry(): void {
    if (this.contextRetrySubscription) {
      this.contextRetrySubscription.unsubscribe();
      this.contextRetrySubscription = null;
    }
  }

  private disconnectIframe(clearSuppression: boolean): void {
    this.hideWidget('disabled');
    this.stopAuthRefresh();
    this.stopSuppressionTimer();
    this.stopContextRetry();
    this.stopTimedRetriggerTimer();
    this.iframe = null;
    this.iframeLoaded = false;
    this.initialContextSent = false;
    this.currentContextId = 0;
    this.nextContextId = 0;
    this.latestContext = null;
    this.suppressionEndPending = false;
    this.homePageActiveSubject.next(false);

    if (clearSuppression) {
      this.suppressionMode = null;
      this.suppressionUntil = null;
      this.suppressedSubject.next(false);
    }
  }

  private handleLogout(): void {
    this.clearPersistedSuppression();
    this.disconnectIframe(true);
  }

  private restorePersistedSuppression(): void {
    let storedValue: string;

    try {
      storedValue = this.window.sessionStorage.getItem(SUPPRESSION_STORAGE_KEY);
    } catch {
      return;
    }

    if (!storedValue) {
      return;
    }

    let suppression: PersistedSuppression;
    try {
      suppression = JSON.parse(storedValue);
    } catch {
      this.clearPersistedSuppression();
      return;
    }

    if (!this.isPersistedSuppression(suppression)) {
      this.clearPersistedSuppression();
      return;
    }

    if (new Date(suppression.until).getTime() <= this.getCurrentTime().getTime()) {
      this.clearPersistedSuppression();
      return;
    }

    this.suppressionMode = suppression.mode;
    this.suppressionUntil = suppression.until;
    this.suppressedSubject.next(true);
  }

  private persistSuppression(): void {
    if (!this.suppressionMode || !this.suppressionUntil) {
      return;
    }

    const suppression: PersistedSuppression = {
      mode: this.suppressionMode,
      until: this.suppressionUntil,
    };

    try {
      this.window.sessionStorage.setItem(SUPPRESSION_STORAGE_KEY, JSON.stringify(suppression));
    } catch {
      return;
    }
  }

  private clearPersistedSuppression(): void {
    try {
      this.window.sessionStorage.removeItem(SUPPRESSION_STORAGE_KEY);
    } catch {
      return;
    }
  }

  private isPersistedSuppression(value: any): value is PersistedSuppression {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const keys = Object.keys(value).sort();
    const isKnownMode = value.mode === 'dismissed' || value.mode === 'snoozed';
    if (!isKnownMode || keys.length !== 2 || keys[0] !== 'mode' || keys[1] !== 'until') {
      return false;
    }

    if (typeof value.until !== 'string') {
      return false;
    }

    const until = new Date(value.until);
    return Number.isFinite(until.getTime()) && until.toISOString() === value.until;
  }

  private getNextCalendarDayStart(now: Date): Date {
    const sastNow = new Date(now.getTime() + SAST_UTC_OFFSET_MS);
    const nextSastDayStartMs =
      Date.UTC(sastNow.getUTCFullYear(), sastNow.getUTCMonth(), sastNow.getUTCDate() + 1) - SAST_UTC_OFFSET_MS;
    return new Date(nextSastDayStartMs);
  }

  private getSnoozeUntil(now: Date): Date {
    const normalSnoozeEndMs = now.getTime() + this.snoozeDurationMs;
    const timedRetriggerMs = this.getNextTimedRetrigger(now).getTime();
    const finalHourStartMs = timedRetriggerMs - TIMED_RETRIGGER_SNOOZE_WINDOW_MS;
    return new Date(normalSnoozeEndMs >= finalHourStartMs ? timedRetriggerMs : normalSnoozeEndMs);
  }

  private getNextTimedRetrigger(now: Date): Date {
    const sastNow = new Date(now.getTime() + SAST_UTC_OFFSET_MS);
    let retriggerMs =
      Date.UTC(
        sastNow.getUTCFullYear(),
        sastNow.getUTCMonth(),
        sastNow.getUTCDate(),
        TIMED_RETRIGGER_HOUR_SAST
      ) - SAST_UTC_OFFSET_MS;

    if (retriggerMs <= now.getTime()) {
      retriggerMs =
        Date.UTC(
          sastNow.getUTCFullYear(),
          sastNow.getUTCMonth(),
          sastNow.getUTCDate() + 1,
          TIMED_RETRIGGER_HOUR_SAST
        ) - SAST_UTC_OFFSET_MS;
    }

    return new Date(retriggerMs);
  }

  private getCurrentTime(): Date {
    return new Date();
  }

  private trackAnalytics(
    eventType: PracticeAgentAnalyticsEventType,
    details: PracticeAgentAnalyticsDetails = {}
  ): void {
    const analyticsDetails: PracticeAgentAnalyticsDetails =
      this.latestContext?.currentPage === 'home'
        ? { ...details, HomeTab: this.currentHomeTab }
        : details;
    this.subscriptions.add(
      this.analyticsService.track(eventType, this.latestContext?.currentPage || 'other', analyticsDetails).subscribe()
    );
  }

  private hideWidget(reason: string): void {
    const wasVisible = this.isEffectivelyVisible();
    this.paRequestedVisibilitySubject.next(false);
    if (wasVisible) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_HIDDEN, { reason });
    }
  }

  private invalidateCurrentContext(reason: string): void {
    this.hideWidget(reason);
    this.currentContextId = 0;
    this.nextContextId++;
  }

  private isEffectivelyVisible(): boolean {
    return (
      this.paRequestedVisibilitySubject.value &&
      this.homePageActiveSubject.value &&
      !this.suppressedSubject.value &&
      this.enabled
    );
  }

  private createAuthMessage(firebaseIdToken: string): HbcAuthChangedMessage {
    return {
      type: 'hbc.auth.changed',
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload: { firebaseIdToken },
    };
  }

  private createSuppressionMessage(): HbcSuppressionChangedMessage {
    return {
      type: 'hbc.suppression.changed',
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload: this.getSuppression(),
    };
  }

  private getSuppression(): PracticeAgentSuppression {
    if (!this.suppressedSubject.value) {
      return { suppressed: false };
    }

    return {
      suppressed: true,
      mode: this.suppressionMode,
      ...(this.suppressionUntil ? { until: this.suppressionUntil } : {}),
    };
  }

  private postMessage(message: HbcPracticeAgentMessage): void {
    if (this.canPostMessage()) {
      this.iframe.contentWindow.postMessage(message, this.widgetOrigin);
    }
  }

  private canPostMessage(): boolean {
    return this.enabled && this.iframeLoaded && !!this.iframe?.contentWindow && !!this.widgetOrigin;
  }

  private trackInvalidMessage(reason: string): void {
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_INVALID_MESSAGE_REJECTED, { reason });
  }

  private trackEvent(event: AppInsightTags, properties: { [name: string]: string } = {}): void {
    this.appInsightsService.trackEvent(event, {
      ...properties,
      contractVersion: PRACTICE_AGENT_MESSAGE_VERSION.toString(),
    });
  }

  private getWidgetOrigin(widgetUrl: string): string | null {
    if (!widgetUrl) {
      return null;
    }

    try {
      const url = new URL(widgetUrl);
      const isDifferentOrigin = url.origin !== this.window.location.origin;

      return isDifferentOrigin && url.protocol === 'https:' ? url.origin : null;
    } catch {
      return null;
    }
  }

  private getSnoozeDurationMs(): number {
    const configuredDuration = this.configService.config.practiceAgent.snoozeDurationMs;
    return Number.isFinite(configuredDuration) && configuredDuration > 0 ? configuredDuration : SNOOZE_DURATION_MS;
  }

  private getSnoozeDurationLabel(): string {
    const hours = this.snoozeDurationMs / SNOOZE_DURATION_MS;
    if (Number.isInteger(hours)) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    return `${this.snoozeDurationMs / 1000} seconds`;
  }

  get validatedWidgetUrl(): string | null {
    return this.widgetOrigin ? this.widgetUrl : null;
  }

  private getContextPatientId(
    currentPage: PracticeAgentPage,
    currentPatientId: string,
    activePatients: Array<{ patientId: string }>
  ): string | null {
    const isPatientPage = ['patient-summary', 'patient-consultation', 'patient-record'].includes(currentPage);
    if (!isPatientPage || !currentPatientId) {
      return null;
    }

    return activePatients?.some(patient => patient.patientId === currentPatientId) ? currentPatientId : null;
  }

  private getCurrentPage(url: string): PracticeAgentPage {
    const path = (url || '').split(/[?#]/)[0];
    const segments = path.split('/').filter(segment => !!segment);
    const area = segments[1];

    if (area === 'home') {
      return 'home';
    }

    if (area === 'provider') {
      return 'provider';
    }

    if (area !== 'patient') {
      return 'other';
    }

    const patientSegment = segments[2];
    const patientPage = segments[3];
    if (!patientSegment || ['add', 'search', 'mobile-search'].includes(patientSegment)) {
      return 'patient-search';
    }

    if (patientPage === 'dashboard') {
      return 'patient-summary';
    }

    if (patientPage === 'consult') {
      return 'patient-consultation';
    }

    return 'patient-record';
  }

  private getCurrentHomeTab(url: string): PracticeAgentHomeTab {
    if (this.getCurrentPage(url) !== 'home') {
      return 'other';
    }

    try {
      const homeTab = new URL(url, this.window.location.origin).searchParams.get('tab') || 'schedule';
      const allowedTabs: PracticeAgentHomeTab[] = [
        'schedule',
        'tracker',
        'history',
        'path',
        'documents',
        'whatsapp',
      ];
      return allowedTabs.includes(homeTab as PracticeAgentHomeTab) ? (homeTab as PracticeAgentHomeTab) : 'other';
    } catch {
      return 'other';
    }
  }
}
